"use client"

import React from 'react';
import Link from 'next/link';
import { useTheme } from '@/components/layout/ThemeProvider';
import OptimizedImage from '@/components/ui/OptimizedImage';
import { CATEGORIAS, NOME_CATEGORIAS } from '@/lib/constants';
import { motion } from 'framer-motion';

// Lista de serviços (mesma do page.tsx para garantir consistência)
const servicos = [
  {
    id: 'desenvolvimento-web',
    titulo: 'Desenvolvimento Web',
    categoria: CATEGORIAS.DESENVOLVIMENTO,
    descricao: 'Sites exclusivos e personalizados com foco em SEO, design moderno e segurança avançada.',
    imagem: '/images/desenvolvimento-bg.jpg',
    destaques: [
      'Websites responsivos para dispositivos móveis',
      'Otimização para SEO e desempenho',
      'Design moderno e intuitivo'
    ]
  },
  {
    id: 'desenvolvimento-sistemas',
    titulo: 'Sistemas Empresariais',
    categoria: CATEGORIAS.DESENVOLVIMENTO,
    descricao: 'Sistemas personalizados e dashboards para gestão e automação de processos empresariais.',
    imagem: '/images/sistemas-bg.jpg',
    destaques: [
      'Sistemas de gestão empresarial',
      'Integrações com APIs externas',
      'Automação de processos'
    ]
  },
  {
    id: 'energia-solar',
    titulo: 'Energia Solar',
    categoria: CATEGORIAS.INFRAESTRUTURA,
    descricao: 'Reduza até 95% da sua conta de luz com soluções completas de energia solar sustentável.',
    imagem: '/images/energia-solar-bg.jpg',
    destaques: [
      'Projetos residenciais e comerciais',
      'Instalação profissional certificada',
      'Retorno do investimento em até 5 anos'
    ]
  },
  {
    id: 'infraestrutura-rede',
    titulo: 'Redes Empresariais',
    categoria: CATEGORIAS.INFRAESTRUTURA,
    descricao: 'Projetos completos de infraestrutura de rede para empresas de todos os portes.',
    imagem: '/images/rede-bg.jpg',
    destaques: [
      'Cabeamento estruturado certificado',
      'Configuração de servidores e firewalls',
      'Redes Wi-Fi empresariais seguras'
    ]
  },
  {
    id: 'manutencao-celulares-tablets',
    titulo: 'Celulares e Tablets',
    categoria: CATEGORIAS.MANUTENCAO,
    descricao: 'Reparos para smartphones e tablets com diagnóstico especializado e garantia.',
    imagem: '/images/manutencao-celulares-bg.jpg',
    destaques: [
      'Troca de tela e componentes',
      'Reparo de placas com microsolda',
      'Recuperação por danos de água'
    ]
  },
  {
    id: 'manutencao-notebooks-pcs',
    titulo: 'Notebooks e PCs',
    categoria: CATEGORIAS.MANUTENCAO,
    descricao: 'Soluções para problemas de hardware e software em computadores e laptops.',
    imagem: '/images/manutencao-pcs-bg.jpg',
    destaques: [
      'Upgrade de hardware e desempenho',
      'Recuperação de dados críticos',
      'Remoção de malware e vírus'
    ]
  },
  {
    id: 'manutencao-videogames',
    titulo: 'Videogames',
    categoria: CATEGORIAS.MANUTENCAO,
    descricao: 'Reparos especializados para consoles de todas as gerações com garantia de serviço.',
    imagem: '/images/videogames-bg.jpg',
    destaques: [
      'Reparo de unidades ópticas',
      'Manutenção preventiva e limpeza',
      'Substituição de componentes'
    ]
  },
  {
    id: 'manutencao-eletronicos',
    titulo: 'Dispositivos Eletrônicos',
    categoria: CATEGORIAS.MANUTENCAO,
    descricao: 'Serviços de reparo para diversos dispositivos eletrônicos com técnicos especializados.',
    imagem: '/images/manutencao-eletronicos-bg.jpg',
    destaques: [
      'Reparos em TVs e equipamentos de áudio',
      'Manutenção de impressoras e periféricos',
      'Conserto de nobreaks e estabilizadores'
    ]
  },
  {
    id: 'automacao-residencial',
    titulo: 'Automação Residencial',
    categoria: CATEGORIAS.INFRAESTRUTURA,
    descricao: 'Transforme sua casa em um ambiente inteligente com sistemas personalizados de controle.',
    imagem: '/images/automacao-bg.jpg',
    destaques: [
      'Controle de iluminação e climatização',
      'Sistemas de segurança integrados',
      'Assistentes de voz e controle remoto'
    ]
  }
];

interface ServicosDetalhesPageProps {
  id: string;
}

const ServicosDetalhesPage = ({ id }: ServicosDetalhesPageProps) => {
  const { theme } = useTheme();
  
  // Encontrar o serviço pelo ID
  const servico = servicos.find(s => s.id === id);
  
  // Se o serviço não for encontrado
  if (!servico) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <h1 className={`text-3xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
          Serviço não encontrado
        </h1>
        <p className={`mb-8 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
          O serviço que você está procurando não está disponível ou foi removido.
        </p>
        <Link 
          href="/servicos" 
          className={`inline-flex items-center px-6 py-3 rounded-lg font-medium ${
            theme === 'dark' ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Voltar para Serviços
        </Link>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen">
      {/* Hero do serviço com imagem de fundo */}
      <div className="relative w-full h-72 md:h-96 overflow-hidden">
        <OptimizedImage
          src={servico.imagem}
          alt={servico.titulo}
          fill
          className="object-cover"
          isLCP
          style={{ minHeight: "100%" }}
        />
        <div className={`absolute inset-0 ${theme === 'dark' ? 'bg-gradient-to-b from-transparent to-gray-900/90' : 'bg-gradient-to-b from-transparent to-gray-900/70'}`}></div>
        
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
          <div className="container mx-auto">
            <div className="flex items-center mb-3">
              <Link 
                href="/servicos" 
                className="inline-flex items-center text-sm text-white hover:text-blue-200 transition-colors"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Serviços
              </Link>
              <span className="mx-2 text-gray-400">/</span>
              <span className="text-sm text-gray-200">{NOME_CATEGORIAS[servico.categoria]}</span>
            </div>
            
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">
              {servico.titulo}
            </h1>
            
            <p className="mt-4 text-lg text-gray-200 max-w-3xl">
              {servico.descricao}
            </p>
          </div>
        </div>
      </div>
      
      {/* Conteúdo principal */}
      <section className={`py-12 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="container mx-auto px-4">
          {/* Destaques do serviço */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {servico.destaques.map((destaque, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className={`p-6 rounded-lg ${
                  theme === 'dark' 
                    ? 'bg-gray-700 border border-gray-600' 
                    : 'bg-gray-50 border border-gray-100'
                }`}
              >
                <div className="flex items-start">
                  <div className={`p-2 rounded-full ${
                    servico.categoria === CATEGORIAS.DESENVOLVIMENTO
                      ? 'bg-green-100 text-green-700' 
                      : servico.categoria === CATEGORIAS.MANUTENCAO
                        ? 'bg-purple-100 text-purple-700'
                        : 'bg-amber-100 text-amber-700'
                  }`}>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <p className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {destaque}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          
          {/* Descrição detalhada */}
          <div className={`prose prose-lg max-w-none ${
            theme === 'dark' ? 'prose-invert prose-headings:text-white prose-p:text-gray-300' : ''
          }`}>
            <h2>Sobre este serviço</h2>
            <p>
              Oferecemos soluções de {servico.titulo.toLowerCase()} com qualidade, rapidez e excelência. 
              Nossa equipe especializada atende necessidades específicas, utilizando as tecnologias mais 
              recentes e metodologias comprovadas.
            </p>
            
            <h3>Por que escolher nosso serviço de {servico.titulo.toLowerCase()}?</h3>
            <ul>
              <li>Profissionais certificados e experientes</li>
              <li>Atendimento personalizado para cada cliente</li>
              <li>Uso de tecnologias de ponta</li>
              <li>Garantia de satisfação em todos os projetos</li>
              <li>Suporte técnico contínuo após a conclusão</li>
            </ul>
            
            <h3>Como funciona</h3>
            <p>
              Nosso processo de {servico.titulo.toLowerCase()} começa com uma análise detalhada das suas necessidades.
              Em seguida, desenvolvemos um plano personalizado que atenda aos seus objetivos específicos,
              implementamos a solução com acompanhamento constante e oferecemos suporte contínuo após a conclusão.
            </p>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className={`py-16 ${theme === 'dark' ? 'bg-gray-900' : 'bg-blue-50'}`}>
        <div className="container mx-auto px-4 text-center">
          <h2 className={`text-3xl font-bold mb-6 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Interessado neste serviço?
          </h2>
          <p className={`text-lg mb-8 max-w-2xl mx-auto ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
            Entre em contato agora mesmo para discutir seu projeto e obter um orçamento personalizado.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.a 
              href="/contato" 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-3 bg-blue-600 text-white font-bold rounded-lg shadow-lg hover:bg-blue-700 transition-all"
            >
              Solicitar Orçamento
            </motion.a>
            <motion.a 
              href="tel:+551932060706" 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`px-8 py-3 font-bold rounded-lg shadow-lg transition-all flex items-center justify-center ${
                theme === 'dark' 
                  ? 'bg-gray-800 text-white hover:bg-gray-700' 
                  : 'bg-white text-gray-900 hover:bg-gray-100'
              }`}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              (19) 3206-0706
            </motion.a>
          </div>
        </div>
      </section>
      
      {/* Serviços relacionados */}
      <section className={`py-12 ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'}`}>
        <div className="container mx-auto px-4">
          <h2 className={`text-2xl font-bold mb-8 text-center ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Outros serviços que podem te interessar
          </h2>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {servicos
              .filter(s => s.id !== id && s.categoria === servico.categoria)
              .slice(0, 3)
              .map((servicoRelacionado) => (
                <motion.div 
                  key={servicoRelacionado.id}
                  whileHover={{ y: -5 }}
                  className="h-full"
                >
                  <Link href={`/servicos/${servicoRelacionado.id}`} className="h-full block">
                    <div className={`h-full rounded-lg overflow-hidden shadow-md ${
                      theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600' : 'bg-white hover:shadow-lg'
                    } transition-all duration-300`}>
                      <div className="relative h-48 overflow-hidden">
                        <OptimizedImage
                          src={servicoRelacionado.imagem}
                          alt={servicoRelacionado.titulo}
                          fill
                          className="object-cover"
                          style={{ minHeight: "100%" }}
                        />
                      </div>
                      <div className="p-4">
                        <h3 className={`font-bold text-lg mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          {servicoRelacionado.titulo}
                        </h3>
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                          {servicoRelacionado.descricao}
                        </p>
                        <div className={`mt-4 text-sm font-medium text-blue-600 flex items-center ${theme === 'dark' ? 'text-blue-400' : ''}`}>
                          Saiba mais
                          <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicosDetalhesPage; 